import styled, { keyframes, css } from 'styled-components';

/* ============================================================
 * Keyframes do redesign
 * ============================================================ */

// CTA "acende" ao habilitar
const ctaPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(77, 20, 140, .3); }
  100% { box-shadow: 0 0 0 8px rgba(77, 20, 140, 0); }
`;

// Disclosure dos campos ao selecionar tipo de pessoa
const fieldsReveal = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Check verde de validação positiva
const checkPop = keyframes`
  from { opacity: 0; transform: scale(.6); }
  to   { opacity: 1; transform: scale(1); }
`;

// Erro inline entrando
const errIn = keyframes`
  from { opacity: 0; transform: translateY(-2px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Flash de preenchimento do CEP
const cepFlash = keyframes`
  from { background-color: var(--brand-50); }
  to   { background-color: transparent; }
`;

/* ============================================================
 * Layout raiz
 * ============================================================ */

// Container principal da aplicação
export const ZeplinAppContainer = styled.div`
  width: 100%;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

// Header
export const ZeplinHeader = styled.header`
  width: 100%;
  height: 72px;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 var(--s-8);
  background: var(--surface);
  border-bottom: 1px solid var(--line-200);
  box-shadow: var(--shadow-xs);
  position: sticky;
  top: 0;
  z-index: 10;

  @media (max-width: 768px) {
    height: 64px;
    padding: 0 var(--s-4);
  }
`;

// Logo estilizado
export const ZeplinLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  a {
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .logo-image {
    height: 28px;
    width: auto;
    transition: transform var(--dur-base) var(--ease);

    &:hover {
      transform: scale(1.05);
    }
  }
`;

// Content area
export const ZeplinContent = styled.main`
  flex: 1 1 auto;
  min-height: calc(100dvh - 72px);
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  background: var(--bg);

  @media (max-width: 768px) {
    min-height: calc(100dvh - 64px);
    padding: 16px;
  }
`;

/* ============================================================
 * Card do formulário
 * ============================================================ */

// Container do formulário
export const ZeplinFormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Card do formulário — superfície sólida com filete de marca no topo
export const ZeplinFormCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: var(--s-6);
  padding: var(--s-8);
  border-radius: var(--r-xl);
  border: 1px solid var(--line-200);
  background: var(--surface);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  margin: 0 auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-gradient);
  }

  @media (max-width: 768px) {
    padding: var(--s-6);
    border-radius: var(--r-lg);
    box-shadow: var(--shadow-md);
  }
`;

/* ============================================================
 * Tipografia
 * ============================================================ */

// Título principal — neutro com filete de acento
export const ZeplinTitle = styled.h1`
  text-align: left;
  color: var(--ink-900);
  font-family: 'Montserrat', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.15;
  margin: 0 0 var(--s-2);

  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 3px;
    border-radius: 2px;
    background: var(--accent-600);
    margin-top: var(--s-3);
  }

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

// Subtítulo
export const ZeplinSubtitle = styled.p`
  text-align: left;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--ink-500);
  margin: 0;
`;

// Subtítulo de seção ("Seus dados", "Endereço")
export const ZeplinDataSubtitle = styled.p`
  text-align: left;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ink-900);
  margin: 0;
`;

/* ============================================================
 * Stepper rotulado horizontal
 * ============================================================ */

export const ZeplinStepIndicator = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  margin: 0;

  .eyebrow {
    font: var(--fs-eyebrow);
    color: var(--ink-500);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .rail {
    display: flex;
    align-items: center;
    gap: var(--s-2);
  }

  .dot {
    width: 28px;
    height: 28px;
    border-radius: var(--r-full);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
    transition: background var(--dur-slow) var(--ease), border-color var(--dur-base);

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .dot.future {
    background: var(--surface);
    border: 2px solid var(--ink-300);
    color: var(--ink-500);
  }

  .dot.current {
    background: var(--brand-600);
    color: #fff;
    box-shadow: 0 0 0 4px var(--brand-100);
  }

  .dot.completed {
    background: var(--brand-600);
    color: #fff;
  }

  .line {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: var(--line-200);
    position: relative;
    overflow: hidden;
  }

  .line .fill {
    position: absolute;
    inset: 0;
    width: 0;
    background: var(--brand-600);
    transition: width var(--dur-slow) var(--ease);
  }

  .line.done .fill {
    width: 100%;
  }

  .labels {
    display: flex;
    justify-content: space-between;
  }

  .labels span {
    font: var(--fs-helper);
    color: var(--ink-500);
  }

  .labels span.on {
    color: var(--ink-900);
    font-weight: 600;
  }
`;

/* ============================================================
 * Campos
 * ============================================================ */

// Container para grupo de campo (label + input)
export const ZeplinFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  text-align: left;
  position: relative;
  margin-bottom: var(--s-5);

  &:last-child {
    margin-bottom: 0;
  }

  /* Variantes lado-a-lado */
  &.row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--s-3);
  }

  &.row-num {
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: var(--s-3);
  }
`;

// Label
export const ZeplinLabel = styled.label`
  display: block;
  align-self: stretch;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  color: var(--ink-700);
  margin-bottom: var(--s-2);
  order: 1;
  flex-shrink: 0;

  /* Mantido por compatibilidade — asteriscos removidos dos componentes */
  .required {
    color: var(--error-600);
    margin-right: 2px;
  }

  .optional {
    color: var(--ink-500);
    font-weight: 400;
  }
`;

// Campos base compartilhados (input + select)
const fieldBase = css`
  height: 52px;
  width: 100%;
  border: 2px solid var(--line-200);
  border-radius: var(--r-md);
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  color: var(--ink-900);
  background: var(--surface);
  transition: border-color var(--dur-base) var(--ease), box-shadow var(--dur-base);
  order: 2;
  flex-shrink: 0;

  &:focus {
    outline: none;
    border-color: var(--brand-600);
    box-shadow: var(--focus-ring);
  }

  &:hover:not(:focus):not(:disabled) {
    border-color: var(--brand-400);
  }

  &::placeholder {
    color: var(--ink-300);
  }

  &:disabled {
    background: var(--gray-100);
    color: var(--ink-500);
    cursor: not-allowed;
  }

  &.is-error {
    border-color: var(--error-600);
    box-shadow: var(--focus-ring-error);
  }

  &.is-valid {
    border-color: var(--success-600);
  }

  &.flash {
    animation: ${cepFlash} var(--dur-slow) var(--ease) 1;
  }
`;

// Select
export const ZeplinSelect = styled.select`
  ${fieldBase}
  padding: 0 48px 0 16px;
  line-height: 1.4;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 16px center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;

  option {
    color: var(--ink-700);
    background: var(--surface);
    padding: 8px;
    line-height: 1.5;
  }
`;

// Input
export const ZeplinInput = styled.input`
  ${fieldBase}
  padding: 0 16px;

  &[type="date"] {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
      margin-left: auto;
      padding: 4px;
    }
  }
`;

// Mensagem de erro inline tokenizada
export const ZeplinError = styled.span`
  font: var(--fs-helper);
  color: var(--error-600);
  margin-top: var(--s-1);
  display: flex;
  gap: 4px;
  align-items: center;
  order: 3;
  animation: ${errIn} var(--dur-base) var(--ease);
`;

// Indicador de sucesso (check verde) posicionado dentro do FieldGroup
export const ZeplinValidCheck = styled.span`
  position: absolute;
  right: 14px;
  top: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--success-600);
  pointer-events: none;
  animation: ${checkPop} var(--dur-base) var(--ease);

  svg {
    width: 20px;
    height: 20px;
  }
`;

/* ============================================================
 * Tiles de seleção PF/PJ
 * ============================================================ */

export const ZeplinTileGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--s-3);
  order: 2;
`;

export const ZeplinTile = styled.label`
  position: relative;
  padding: var(--s-4);
  border: 2px solid var(--line-200);
  border-radius: var(--r-md);
  background: var(--surface);
  min-height: 88px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  transition: all var(--dur-base) var(--ease);

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .tile-icon {
    width: 24px;
    height: 24px;
    color: var(--ink-500);
    transition: color var(--dur-base) var(--ease);
  }

  .tile-title {
    font: var(--fs-label);
    color: var(--ink-900);
    transition: color var(--dur-base) var(--ease);
  }

  .tile-sub {
    font: var(--fs-helper);
    color: var(--ink-500);
  }

  .tile-check {
    position: absolute;
    top: var(--s-3);
    right: var(--s-3);
    width: 16px;
    height: 16px;
    color: var(--success-600);
    opacity: 0;
    transition: opacity var(--dur-base) var(--ease);
  }

  &:hover,
  &:focus-within {
    border-color: var(--brand-400);
  }

  &.selected {
    border-color: var(--brand-600);
    background: var(--brand-50);
    box-shadow: var(--focus-ring);

    .tile-icon,
    .tile-title {
      color: var(--brand-700);
    }

    .tile-check {
      opacity: 1;
    }
  }
`;

// Wrapper com disclosure animado para os campos que aparecem
export const ZeplinFieldsReveal = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fieldsReveal} var(--dur-slow) var(--ease);
`;

// Chip "Endereço encontrado" (CEP mágico)
export const ZeplinCepChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  margin-top: var(--s-1);
  background: var(--success-50);
  color: var(--success-600);
  font: var(--fs-helper);
  padding: 2px 8px;
  border-radius: var(--r-full);
  order: 3;
  animation: ${errIn} var(--dur-base) var(--ease);
`;

/* ============================================================
 * Botão
 * ============================================================ */

export const ZeplinButton = styled.button`
  width: 100%;
  height: 52px;
  flex-grow: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: var(--s-2);
  padding: 0 var(--s-8);
  border-radius: var(--r-md);
  border: none;
  background-image: var(--brand-gradient);
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background-image var(--dur-base) var(--ease),
    transform var(--dur-fast) var(--ease), box-shadow var(--dur-base) var(--ease);

  &:hover:not(:disabled) {
    background-image: var(--brand-gradient-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-cta);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.99);
  }

  &:disabled {
    background-image: none;
    background-color: var(--line-200);
    color: var(--ink-500);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }

  &.enabled {
    animation: ${ctaPulse} var(--dur-slow) var(--ease) 1;
  }

  &.secondary {
    background: transparent;
    background-image: none;
    color: var(--brand-700);
    border: 2px solid var(--line-200);

    &:hover:not(:disabled) {
      background: var(--bg);
      box-shadow: none;
      transform: none;
    }
  }
`;
